import { createFileRoute } from "@tanstack/react-router";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { PRODUCTS } from "@/lib/catalog";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ProductCard } from "@/components/shop/ProductCard";
import { ContactCta } from "@/components/site/ContactCta";

export const Route = createFileRoute("/aanbiedingen")({
  head: () => ({
    meta: [
      { title: "Voordeelsets | Elvion Group Nederland" },
      {
        name: "description",
        content:
          "Bekijk samengestelde voordeelsets van Elvion Group Nederland: beveiligingssets, camera's en netwerkproducten met duidelijke productinformatie.",
      },
      { property: "og:title", content: "Voordeelsets | Elvion Group Nederland" },
      {
        property: "og:description",
        content:
          "Bekijk samengestelde voordeelsets en losse producten met duidelijke specificaties, verzending en garantie.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Voordeelsets | Elvion Group Nederland" },
      { name: "twitter:description", content: "Samengestelde voordeelsets met duidelijke productinformatie, verzending en garantie." },
      { property: "og:url", content: "https://elvion-group.nl/aanbiedingen" },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/aanbiedingen" }],
    scripts: [
      breadcrumbJsonLd([{ name: "Aanbiedingen", path: "/aanbiedingen" }]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://elvion-group.nl/aanbiedingen#faq",
          mainEntity: FAQ_ITEMS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: AanbiedingenPage,
});

/** Vaste volgorde van slugs die op de aanbiedingenpagina getoond worden.
 *  Zo blijft de layout stabiel en kunnen we handmatig extra deals toevoegen. */
const FEATURED_SLUGS = ["hikvision-4cam-nvr-set-4k"];

/** FAQ-items voor de aanbiedingenpagina. Gebruikt voor zowel de zichtbare
 *  sectie als de FAQPage JSON-LD (rich results in Google). */
const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: "Zijn de prijzen van de voordeelsets inclusief btw?",
    a: "Ja, alle prijzen op de aanbiedingenpagina zijn inclusief 21% btw. Op de factuur wordt het btw-bedrag apart gespecificeerd.",
  },
  {
    q: "Wat zit er standaard in een voordeelset?",
    a: "Een voordeelset bestaat uit vooraf geselecteerde camera's, een recorder (NVR) en de benodigde accessoires. De exacte samenstelling staat vermeld bij elk product.",
  },
  {
    q: "Kan ik een voordeelset laten installeren door Elvion Group?",
    a: "Ja, wij bieden landelijk installatie aan door onze eigen monteurs. Vraag bij de bestelling om een installatie-offerte op maat.",
  },
  {
    q: "Wat is de levertijd van een voordeelset?",
    a: "Voorradige sets worden doorgaans binnen 1-2 werkdagen verzonden. Bij bestellingen met installatie plannen wij samen een geschikte datum in.",
  },
  {
    q: "Hoeveel garantie krijg ik op de producten in een voordeelset?",
    a: "Op alle Hikvision-producten in onze voordeelsets geldt minimaal 2 jaar fabrieksgarantie. Bij installatie door Elvion Group krijgt u daarnaast garantie op de montage.",
  },
  {
    q: "Kan ik een voordeelset aanpassen of uitbreiden?",
    a: "Ja, u kunt extra camera's, grotere harde schijven of accessoires toevoegen. Neem contact op voor een aangepaste offerte.",
  },
];

function AanbiedingenPage() {
  const featured = FEATURED_SLUGS
    .map((slug) => PRODUCTS.find((p) => p.slug === slug))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  // Vul aan met andere producten die daadwerkelijk in prijs zijn verlaagd.
  const extra = PRODUCTS.filter(
    (p) =>
      !FEATURED_SLUGS.includes(p.slug) &&
      typeof p.old_price_cents === "number" &&
      p.old_price_cents! > p.list_price_cents,
  );

  const items = [...featured, ...extra];

  return (
    <>
      <Header />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <header className="mb-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-orange">
            Aanbiedingen
          </p>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Samengestelde voordeelsets van Elvion Group Nederland
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-foreground/70">
            Sets en losse producten met duidelijke specificaties, inclusief-btw prijzen, verzending en garantievoorwaarden.
          </p>
        </header>

        {items.length === 0 ? (
          <p className="rounded-lg border border-border bg-muted/40 p-6 text-center text-sm text-foreground/70">
            Momenteel zijn er geen samengestelde voordeelsets beschikbaar. Neem contact met ons op voor productadvies.
          </p>
        ) : (
          <section aria-labelledby="aanbiedingen-sets">
            <h2 id="aanbiedingen-sets" className="sr-only">Beschikbare voordeelsets</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </div>
          </section>
        )}

        <section aria-labelledby="aanbiedingen-faq" className="mt-16">
          <div className="mx-auto max-w-3xl">
            <h2
              id="aanbiedingen-faq"
              className="text-center text-2xl font-bold tracking-tight text-foreground sm:text-3xl"
            >
              Veelgestelde vragen over onze voordeelsets
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-center text-sm text-foreground/70">
              Antwoorden op de meest gestelde vragen over prijzen, levering, installatie en garantie.
            </p>

            <dl className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card">
              {FAQ_ITEMS.map((f) => (
                <details key={f.q} className="group px-5 py-4 open:bg-muted/30">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-sm font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                    <dt>{f.q}</dt>
                    <span
                      aria-hidden="true"
                      className="text-orange transition-transform group-open:rotate-45"
                    >
                      +
                    </span>
                  </summary>
                  <dd className="mt-3 text-sm leading-relaxed text-foreground/75">
                    {f.a}
                  </dd>
                </details>
              ))}
            </dl>
          </div>
        </section>
      </main>
      <ContactCta />
      <Footer />
    </>
  );
}
